// File: /frontend/src/pages/user/ShopDetailPage.js

import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import StarRating from '../../components/common/StarRating';
import { getShop, getShopProducts, rateShop } from '../../services/shopService';
import UserLayout from '../../components/layouts/UserLayout';
import { getMarketplaceProducts } from '../../services/productService';
import WishlistButton from '../../components/common/WishlistButton';
import ShareSheet from '../../components/common/ShareSheet';
import { Share2 } from 'lucide-react';

const ShopDetailPage = () => {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [rating, setRating] = useState(0);
  const [isRating, setIsRating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showShareOptions, setShowShareOptions] = useState(false);

  // 🎯 NEW: Helper function to get shop image
  const getShopImage = (shop) => {
    if (shop?.shop?.mainImage) {
      return shop.shop.mainImage;
    }
    if (shop?.shop?.images && shop.shop.images.length > 0) {
      return shop.shop.images[0];
    }
    return null;
  };

  // 🎯 NEW: Get all shop images
  const getShopImages = (shop) => {
    if (shop?.shop?.images && shop.shop.images.length > 0) {
      return shop.shop.images;
    }
    return [];
  };

  // Move function definitions above useEffect
  const fetchShopDetails = async () => {
    setLoading(true);
    try {
      // Fetch the shop directly by id via the dedicated GET /api/shops/:id
      // endpoint. This returns the seller regardless of verification status or
      // coordinates. (Previously this scanned the nearby-shops list, which
      // excluded unverified / no-coordinate sellers → false "Shop Not Found".)
      const response = await getShop(shopId);
      if (response.success && response.data) {
        const foundShop = response.data;
        console.log('🏪 Loaded shop:', {
          id: foundShop._id,
          name: foundShop.shop?.name,
          hasMainImage: !!foundShop.shop?.mainImage,
          imageCount: foundShop.shop?.images?.length || 0
        });
        setShop(foundShop);
      } else {
        // Leave shop null so the "Shop Not Found" screen renders.
        toast.error(response.message || 'Shop not found');
      }
    } catch (error) {
      // A 404 means the shop genuinely doesn't exist; keep shop null so the
      // "Shop Not Found" UI shows. Only surface non-404 errors as a toast.
      console.error('Error fetching shop details:', error);
      if (error?.message && error.message !== 'Shop not found') {
        toast.error(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchShopProducts = async () => {
    setLoadingProducts(true);
    try {
      console.log('🏪 [ShopDetailPage] Fetching products for shop:', shopId);
      
      // 🎯 FIXED: Use the correct API call with seller filter.
      // Load the shop's full active catalogue in one page — this grid has no
      // pagination / "load more", so a small limit (was 20) silently hid the
      // rest of the seller's products and made the count mismatch the uploads.
      const response = await getMarketplaceProducts({
        seller: shopId, // 🎯 CRITICAL: Filter by seller ID
        limit: 1000,
        page: 1,
        status: 'active' // Only get active products
      });
      
      console.log('🏪 [ShopDetailPage] Shop products API response:', {
        success: response.success,
        count: response.count,
        total: response.total,
        productsFound: response.data?.length || 0,
        shopId: shopId
      });

      if (response.success) {
        setProducts(response.data || []);
        
        // 🎯 ENHANCED: Log detailed results
        console.log(`✅ [ShopDetailPage] Successfully fetched ${response.data?.length || 0} products for shop ${shopId}`);
        
        if (response.data && response.data.length > 0) {
          console.log('🏪 [ShopDetailPage] Sample products:', response.data.slice(0, 2).map(p => ({
            id: p._id,
            name: p.name,
            seller: p.seller?._id,
            sellerMatches: p.seller?._id === shopId
          })));
        } else {
          console.log('📦 [ShopDetailPage] No products found for this shop');
        }
      } else {
        console.error('❌ [ShopDetailPage] Failed to fetch shop products:', response.message);
        toast.error(response.message || 'Failed to fetch shop products');
      }
    } catch (error) {
      console.error('❌ [ShopDetailPage] Error fetching shop products:', error);
      toast.error('Something went wrong while loading shop products');
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    fetchShopDetails();
    fetchShopProducts();
  }, [shopId]);

  const handleRateShop = async () => {
    if (rating === 0) {
      toast.warning('Please select a rating');
      return;
    }

    try {
      const response = await rateShop(shopId, rating);
      if (response.success) {
        toast.success('Rating submitted successfully');
        setIsRating(false);
        // Refresh shop details to get updated rating
        fetchShopDetails();
      } else {
        toast.error(response.message || 'Failed to submit rating');
      }
    } catch (error) {
      console.error('Error rating shop:', error);
      toast.error('Something went wrong');
    }
  };

  // 🎯 NEW: Handle image navigation
  const nextImage = () => {
    const images = getShopImages(shop);
    if (images.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }
  };

  const prevImage = () => {
    const images = getShopImages(shop);
    if (images.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    }
  };

  if (loading) {
    return (
      <UserLayout>
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50/30 flex justify-center items-center">
          <div className="text-center">
            <div className="relative mb-8">
              <div className="w-24 h-24 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin mx-auto"></div>
              <div className="absolute inset-0 w-24 h-24 border-4 border-transparent border-t-amber-400 rounded-full animate-spin mx-auto" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/50 p-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Loading Shop Details</h3>
              <p className="text-gray-600">Please wait while we fetch the shop information...</p>
            </div>
          </div>
        </div>
      </UserLayout>
    );
  }

  if (!shop) {
    return (
      <UserLayout>
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-red-50/30 flex justify-center items-center">
          <div className="text-center">
            <div className="w-32 h-32 bg-gradient-to-br from-red-100 to-red-200 rounded-3xl flex items-center justify-center mx-auto mb-8">
              <svg className="w-16 h-16 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/50 p-8 max-w-md">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Shop Not Found</h2>
              <p className="text-gray-600 mb-8">The shop you're looking for doesn't exist or has been removed.</p>
              <Link 
                to="/user/dashboard" 
                className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 inline-flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Return to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </UserLayout>
    );
  }

  const shopImages = getShopImages(shop);
  const hasMultipleImages = shopImages.length > 1;

  return (
    <UserLayout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50/30">
        <div className="max-w-7xl mx-auto px-3 py-4">

          {/* Premium Shop Banner with Image Gallery */}
          <div className="relative mb-6">
            <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 to-amber-500 rounded-3xl blur opacity-20"></div>
            <div className="relative bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/50 overflow-hidden">
              
              {/* Hero Image Section */}
              <div className="relative h-56 bg-gradient-to-br from-orange-500 via-orange-600 to-amber-600 overflow-hidden">
                {shopImages.length > 0 ? (
                  <div className="relative h-full">
                    <img 
                      src={shopImages[currentImageIndex]} 
                      alt={`${shop.shop?.name} - Image ${currentImageIndex + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                    
                    {/* Premium Image Navigation */}
                    {hasMultipleImages && (
                      <>
                        <button
                          onClick={prevImage}
                          className="absolute left-2 top-1/2 transform -translate-y-1/2 w-9 h-9 bg-white/20 backdrop-blur-lg hover:bg-white/30 text-white rounded-full shadow-xl hover:shadow-2xl transition-all duration-200 flex items-center justify-center group"
                        >
                          <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <button
                          onClick={nextImage}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 w-9 h-9 bg-white/20 backdrop-blur-lg hover:bg-white/30 text-white rounded-full shadow-xl hover:shadow-2xl transition-all duration-200 flex items-center justify-center group"
                        >
                          <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                        
                        {/* Enhanced Image Indicators */}
                        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-3">
                          {shopImages.map((_, index) => (
                            <button
                              key={index}
                              onClick={() => setCurrentImageIndex(index)}
                              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                                index === currentImageIndex 
                                  ? 'bg-white scale-125 shadow-lg' 
                                  : 'bg-white/50 hover:bg-white/70'
                              }`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center text-white">
                      <div className="w-32 h-32 bg-white/20 backdrop-blur-sm rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                        <svg className="w-16 h-16 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                      {/* The shop name is NOT printed here. The info overlay
                          below renders it in an <h1> unconditionally, so having
                          it in this no-images fallback too made every image-less
                          shop show the name twice, overlapping. */}
                      <p className="text-white/80 text-lg">Premium Fashion Store</p>
                    </div>
                  </div>
                )}
                
                {/* Premium Shop Info Overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4 text-white">
                  <div className="flex flex-col gap-3">
                    <div className="min-w-0">
                      <h1 className="text-2xl font-bold mb-2 line-clamp-2">{shop.shop?.name}</h1>
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="bg-white/20 backdrop-blur-lg px-3 py-1.5 rounded-full border border-white/30 font-medium">
                          <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                          </svg>
                          {shop.shop?.category} Fashion
                        </span>
                        {shopImages.length > 1 && (
                          <span className="bg-white/20 backdrop-blur-lg px-3 py-1.5 rounded-full border border-white/30 font-medium flex items-center">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            {shopImages.length} Photos
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Only show a rating when the shop actually has one. This
                        used to fall back to `|| 4.8` and `|| 24`, so every shop
                        with zero reviews advertised "4.8 stars, 24 reviews" to
                        buyers — invented social proof, not a styling default. */}
                    <div className="text-left bg-white/10 backdrop-blur-lg rounded-2xl p-3 border border-white/20 self-start shrink-0">
                      {shop.numReviews > 0 ? (
                        <>
                          <StarRating
                            rating={shop.averageRating || 0}
                            numReviews={shop.numReviews}
                            showCount={true}
                            className="text-white"
                          />
                          <p className="text-white/80 text-sm mt-2">Customer Rating</p>
                        </>
                      ) : (
                        <p className="text-white/80 text-sm">No ratings yet</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Shop actions. Change 9 removed the Shop Information and
                  Business Hours cards from the buyer view — only the shop
                  name and rating (in the header above) remain. */}
              <div className="px-4 pb-4">
                <button
                  onClick={() => setShowShareOptions(true)}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                >
                  <Share2 className="w-4 h-4" />
                  Share this shop
                </button>
              </div>
            </div>
          </div>
          
          {/* Premium Shop Products Section */}
          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-3xl blur opacity-20"></div>
            <div className="relative bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/50 p-4">

              {/* Section Header */}
              <div className="flex flex-row items-center justify-between gap-3 mb-6">
                <div className="flex items-center min-w-0">
                  <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-2xl flex items-center justify-center shadow-lg mr-3 shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-xl font-bold bg-gradient-to-r from-gray-900 via-indigo-800 to-purple-600 bg-clip-text text-transparent">
                      Our Products
                    </h2>
                    <p className="text-gray-600 mt-1 text-sm">Discover our premium collection</p>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-3 py-2 rounded-xl border border-indigo-200 shrink-0">
                  <div className="text-xs text-indigo-600 font-semibold whitespace-nowrap">
                    {loadingProducts ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-500 mr-2"></div>
                        Loading...
                      </div>
                    ) : (
                      `${products.length} items`
                    )}
                  </div>
                </div>
              </div>
              
              {loadingProducts ? (
                <div className="text-center py-20">
                  <div className="relative mb-8">
                    <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-500 rounded-full animate-spin mx-auto"></div>
                    <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-purple-400 rounded-full animate-spin mx-auto" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-6 max-w-md mx-auto">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Loading Products</h3>
                    <p className="text-gray-600">Please wait while we fetch the latest products...</p>
                  </div>
                </div>
              ) : products.length > 0 ? (
                <div className="grid grid-cols-2 gap-3">
                  {products.map(product => (
                    <Link key={product._id} to={`/user/product/${product._id}`} className="block group">
                      <div className="relative h-full">
                        <div className="absolute -inset-1 bg-gradient-to-r from-gray-200 to-gray-300 rounded-2xl blur opacity-30 group-hover:opacity-50 transition duration-300"></div>
                        <div className="relative h-full flex flex-col bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 overflow-hidden hover:shadow-2xl transition-all duration-300 transform group-hover:scale-105">

                          {/* Product Image */}
                          <div className="relative h-40 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
                            {product.images && product.images[0] ? (
                              <img
                                src={product.images[0]}
                                alt={product.name}
                                className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                              />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center text-gray-400">
                                <div className="text-center">
                                  <svg className="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                  <span className="text-sm font-medium">No Image</span>
                                </div>
                              </div>
                            )}
                            
                            {/* Premium Product Badges */}
                            <div className="absolute top-2 left-2 flex flex-col items-start gap-1.5">
                              {product.isTrending && (
                                <span className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-2 py-0.5 rounded-full text-[10px] font-bold shadow-lg whitespace-nowrap">
                                  🔥 Trending
                                </span>
                              )}
                              {product.isLimitedEdition && (
                                <span className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white px-2 py-0.5 rounded-full text-[10px] font-bold shadow-lg whitespace-nowrap">
                                  ⭐ Limited
                                </span>
                              )}
                            </div>

                            {/* Wishlist Button */}
                            <div className="absolute top-2 right-2 z-10">
                              <WishlistButton 
                                productId={product._id} 
                                size="sm"
                                className="shadow-lg"
                              />
                            </div>
                          </div>
                          
                          {/* Product Details */}
                          <div className="p-3 flex flex-col flex-1">
                            <h3 className="font-bold text-sm text-gray-800 mb-2 line-clamp-2 group-hover:text-indigo-600 transition-colors">
                              {product.name}
                            </h3>

                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-3 mt-auto">
                              <span className="text-indigo-600 font-bold text-base">₹{product.zammerPrice}</span>
                              {product.mrp > product.zammerPrice && (
                                <span className="text-gray-500 text-xs line-through">₹{product.mrp}</span>
                              )}
                              {product.mrp > product.zammerPrice && (
                                <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap">
                                  {Math.round(((product.mrp - product.zammerPrice) / product.mrp) * 100)}% OFF
                                </span>
                              )}
                            </div>

                            {/* View Product Button */}
                            <button className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white py-2 px-3 rounded-xl font-semibold text-sm shadow-lg hover:shadow-xl transition-all duration-200 group-hover:scale-105">
                              View Product
                            </button>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-32 h-32 bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl flex items-center justify-center mx-auto mb-8">
                    <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-8 max-w-md mx-auto">
                    <h3 className="text-2xl font-bold text-gray-800 mb-4">No Products Available</h3>
                    <p className="text-gray-600 mb-6">This shop hasn't added any products yet. Check back later for exciting new arrivals!</p>
                    <Link 
                      to="/user/nearby-shops" 
                      className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white py-3 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 inline-flex items-center"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      Browse Other Shops
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ShareSheet
        isOpen={showShareOptions}
        onClose={() => setShowShareOptions(false)}
        url={`${window.location.origin}/shop/${shopId}`}
        title={shop?.shop?.name || 'Shop on Zammer'}
        text={`Check out ${shop?.shop?.name || 'this shop'} on Zammer!`}
      />
    </UserLayout>
  );
};

export default ShopDetailPage;